import { useEffect } from 'react';
import { HouseIcon, ListFilterIcon } from 'lucide-react';
import { Badge } from '@rocco/components/badge';
import { Checkbox } from '@rocco/components/checkbox';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { Spinner } from '@rocco/components/spinner';
import type { BuildHubFragmentFragment } from 'generated/graphql';
import type { BuildHubProject } from '../../types';
import { useConversationFilters } from '../../hooks/use-conversation-filters';

/**
 * A button and popover dropdown that allows the user to filter the chat inbox conversations.
 * @see {@link ChatInboxList}
 */
export function ChatInboxFilter({
    selectedProjects = [],
    onProjectsChange,
}: {
    selectedProjects?: BuildHubProject[];
    onProjectsChange?: (projects: BuildHubProject[]) => void;
}) {
    const { filters, isLoading } = useConversationFilters();
    const projects = filters?.projects;

    function handleProjectSelected(projectID: string, selected: boolean) {
        if (!projects) return;
        if (selected) {
            onProjectsChange?.(
                projects.filter(
                    (p) =>
                        p.ID === projectID ||
                        selectedProjects.some((sp) => sp.ID === p.ID)
                )
            );
        } else {
            onProjectsChange?.(
                selectedProjects.filter((p) => p.ID !== projectID)
            );
        }
    }

    // When the projects list changes, deselect selected projects not in the
    // new list. This makes sure there are no missing selected projects.
    useEffect(() => {
        onProjectsChange?.(
            projects?.filter((p) =>
                selectedProjects.some((sp) => sp.ID === p.ID)
            ) ?? []
        );
        // Do not pass `selectedProjectIDs` and `onProjectsChange` on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    className="relative flex size-12 items-center justify-center rounded-full hover:bg-black/10"
                    aria-label="Filter conversations"
                >
                    <ListFilterIcon className="size-6 text-black/70" />
                    {selectedProjects.length > 0 && (
                        <Badge
                            className="absolute -right-1 -top-1"
                            color="red"
                            size="centeredCompact"
                        >
                            {selectedProjects.length}
                        </Badge>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="flex max-h-[min(52rem,var(--radix-popover-content-available-height))] w-88 flex-col rounded-lg p-0"
                align="end"
                variant="shadcn"
                background="none"
            >
                <h3 className="relative flex items-center gap-2 p-4 text-3.5 leading-tight">
                    <span className="font-medium">Project</span>
                    {isLoading && <Spinner className="size-3" />}
                    {selectedProjects.length > 0 && (
                        <button
                            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg px-2.5 py-1 hover:bg-black/10 focus-visible:outline focus-visible:outline-1 focus-visible:outline-input"
                            onClick={() => onProjectsChange?.([])}
                        >
                            Clear
                        </button>
                    )}
                </h3>
                {projects && (
                    <ul
                        className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
                        role="listbox"
                    >
                        {projects.length > 0 ? (
                            projects.map((project) => (
                                <BuildHubOption
                                    key={project.ID}
                                    project={project}
                                    isSelected={selectedProjects.some(
                                        (sp) => sp.ID === project.ID
                                    )}
                                    onSelectedChange={(selected) =>
                                        handleProjectSelected(
                                            project.ID,
                                            selected
                                        )
                                    }
                                />
                            ))
                        ) : (
                            <li className="px-4 pb-4 text-4 leading-tight">
                                No projects to filter by
                            </li>
                        )}
                    </ul>
                )}
            </PopoverContent>
        </Popover>
    );
}

function BuildHubOption({
    project,
    isSelected,
    onSelectedChange,
}: {
    project: BuildHubFragmentFragment;
    isSelected: boolean;
    onSelectedChange: (selected: boolean) => void;
}) {
    return (
        <li
            key={project.ID}
            className="flex cursor-pointer items-center gap-2 px-4 py-2 hover:bg-black/10 focus:bg-black/5"
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            onClick={() => onSelectedChange(!isSelected)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectedChange(!isSelected);
                }
            }}
        >
            {project.ThumbnailFile?.Thumbnail.Desktop ? (
                <img
                    src={project.ThumbnailFile.Thumbnail.Desktop}
                    alt={project.Title}
                    className="size-8 rounded-full object-cover"
                />
            ) : (
                <div className="flex size-8 items-center justify-center rounded-full bg-white">
                    <HouseIcon className="size-5" />
                </div>
            )}
            <span className="min-w-0 flex-1 truncate text-4 font-medium leading-tight">
                {project.Title}
            </span>
            <Checkbox
                className="pointer-events-none"
                checked={isSelected}
                tabIndex={-1}
            />
        </li>
    );
}
